import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ApplicationRef,
  Component,
  createComponent,
  effect,
  ElementRef,
  EnvironmentInjector,
  inject,
  input,
  OnDestroy,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  GeoJSONSource,
  LngLatBounds,
  LngLatLike,
  MapLayerMouseEvent,
  Map as MapLibreMap,
  NavigationControl,
  Popup,
  setWorkerUrl,
} from 'maplibre-gl';
import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { SERVICE_STATUS_COLORS, ServiceStatus } from '../../lib/constants';
import {
  addAwsMapApiKey,
  buildAwsMapStyle,
  buildMarkerFeatureCollection,
} from '../../lib/functions';
import { Category, MapMarker } from '../../lib/types';
import { MapPopupContentComponent } from './components/map-popup-content/map-popup-content.component';

/**
 * World Bounds limits the map to the world based on the latitude and longitude.
 * Longitude is nudged in from the exact +/-180 extremes (a full 360deg span) —
 * maplibre-gl@6.11.2 throws "Cannot read properties of null (reading '0')"
 * inside its internal resize/constrain matrix calculation when maxBounds spans
 * exactly the full globe. Confirmed via a minimal repro; not AWS/style-related.
 */
const WORLD_BOUNDS: [[number, number], [number, number]] = [
  [-179.9, -85.05112878],
  [179.9, 85.05112878],
];

const SOURCE_ID = 'locations';
const POINT_LAYER_ID = `${SOURCE_ID}-point`;

/**
 * maplibre-gl resolves its worker relative to its own module URL, which
 * breaks once the app is bundled (the worker chunk doesn't end up next to
 * the emitted JS). Self-host the matching worker as a static asset, under
 * a versioned path so cached workers can't mismatch the app bundle.
 */
const WORKER_URL = new URL(
  'assets/maplibre-gl/6.14.0/maplibre-gl-worker.mjs',
  document.baseURI,
).href;

@Component({
  selector: 'app-maplibre-map',
  imports: [CommonModule],
  templateUrl: './maplibre-map.component.html',
  styleUrls: ['./maplibre-map.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MaplibreMapComponent implements AfterViewInit, OnDestroy {
  mapElement = viewChild<ElementRef<HTMLDivElement>>('map');

  readonly categories = input<Category[]>([]);

  readonly markers = input<MapMarker[]>();

  private readonly envInjector = inject(EnvironmentInjector);

  private readonly appRef = inject(ApplicationRef);

  private readonly router = inject(Router);

  private readonly showingPopupSubject = new BehaviorSubject(false);

  public showingPopup$: Observable<boolean> =
    this.showingPopupSubject.asObservable();

  private resizeObserver!: ResizeObserver;

  private map!: MapLibreMap;

  private sourceLoaded = false;

  private activePopup: Popup | null = null;

  constructor() {
    effect(() => {
      // React to changes in markers and categories signals
      this.markers();
      this.categories();

      if (this.map && this.sourceLoaded) {
        this.updateSourceData();
      }
    });
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.activePopup?.remove();
    this.map?.remove();
  }

  private initMap(): void {
    setWorkerUrl(WORKER_URL);

    const style = buildAwsMapStyle({
      awsMapApiKey: environment.awsMapApiKey,
      awsMapRegion: environment.awsMapRegion,
    });

    this.map = new MapLibreMap({
      container: this.mapElement()!.nativeElement,
      style,
      transformRequest: (url) => ({
        url: addAwsMapApiKey(url, environment.awsMapApiKey),
      }),
      center: [-95.7129, 37.0902], // Centered on the USA
      zoom: 4,
      maxBounds: WORLD_BOUNDS,
    });

    this.map.addControl(new NavigationControl(), 'top-left');

    this.map.on('load', () => this.addPointsSource());

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      this.map.resize();
    });

    const mapElement = this.mapElement();
    if (mapElement) {
      this.resizeObserver.observe(mapElement.nativeElement);
    }
  }

  private addPointsSource(): void {
    this.map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: buildMarkerFeatureCollection(this.markers() ?? []),
    });

    this.map.addLayer({
      id: POINT_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      layout: {
        // Worse statuses draw on top when points overlap, so a DOWN dot
        // is never hidden under a healthy one at the same location.
        'circle-sort-key': [
          'match',
          ['get', 'status'],
          ServiceStatus.DOWN,
          3,
          ServiceStatus.IMPAIRED,
          2,
          ServiceStatus.ON_BACKUP,
          2,
          1,
        ],
      },
      paint: {
        'circle-color': [
          'match',
          ['get', 'status'],
          ServiceStatus.DOWN,
          SERVICE_STATUS_COLORS[ServiceStatus.DOWN],
          ServiceStatus.IMPAIRED,
          SERVICE_STATUS_COLORS[ServiceStatus.IMPAIRED],
          ServiceStatus.ON_BACKUP,
          SERVICE_STATUS_COLORS[ServiceStatus.ON_BACKUP],
          SERVICE_STATUS_COLORS[ServiceStatus.UP],
        ],
        'circle-radius': 9,
        'circle-stroke-width': 2,
        'circle-stroke-color': 'rgba(255, 255, 255, 0.9)',
      },
    });

    this.registerInteractions();

    this.sourceLoaded = true;
    this.updateSourceData();
  }

  /**
   * MapLibre's layer-scoped events do their own GPU-side hit-testing
   * against the rendered layer — no manual DOM marker bookkeeping or
   * polling needed, and no risk of a click missing a marker that's
   * mid-rebuild.
   */
  private registerInteractions(): void {
    this.map.on('click', POINT_LAYER_ID, (e: MapLayerMouseEvent) => {
      const feature = e.features?.[0];
      const markerIndex = feature?.properties?.['markerIndex'];
      if (markerIndex == null) return;

      const mapMarker = (this.markers() ?? [])[markerIndex as number];
      if (mapMarker) {
        this.openPopup(mapMarker, [+mapMarker.longitude, +mapMarker.latitude]);
      }
    });

    this.map.on('mouseenter', POINT_LAYER_ID, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', POINT_LAYER_ID, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  private updateSourceData(): void {
    const source = this.map.getSource<GeoJSONSource>(SOURCE_ID);
    const markers = this.markers() ?? [];
    source?.setData(buildMarkerFeatureCollection(markers));
    this.fitToMarkers(markers);
  }

  private fitToMarkers(markers: MapMarker[]): void {
    if (!markers.length) return;

    const bounds = markers.reduce(
      (b, marker) => b.extend([+marker.longitude, +marker.latitude]),
      new LngLatBounds(),
    );
    this.map.fitBounds(bounds, { padding: 40 });
  }

  private openPopup(mapMarker: MapMarker, lngLat: LngLatLike): void {
    this.activePopup?.remove();

    const componentRef = createComponent(MapPopupContentComponent, {
      environmentInjector: this.envInjector,
    });
    componentRef.setInput('mapMarker', mapMarker);
    this.appRef.attachView(componentRef.hostView);
    // The click that gets here originates from MapLibre's own DOM listener,
    // outside Angular's zone — without an explicit tick the attached view
    // never renders, so the popup would show up empty (invisible in
    // practice, since maplibre-gl's popup collapses around empty content).
    componentRef.changeDetectorRef.detectChanges();
    componentRef.instance.viewServices.subscribe((serviceName) => {
      this.navigateToServices(serviceName);
      this.activePopup?.remove();
    });

    const popup = new Popup({
      closeButton: true,
      closeOnClick: true,
      maxWidth: 'none',
    })
      .setLngLat(lngLat)
      .setDOMContent(componentRef.location.nativeElement)
      .addTo(this.map);

    popup.on('close', () => {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      this.showingPopupSubject.next(false);
      if (this.activePopup === popup) {
        this.activePopup = null;
      }
    });

    this.activePopup = popup;
    this.showingPopupSubject.next(true);
  }

  private navigateToServices(serviceName: string): void {
    this.router.navigate(['/services'], {
      queryParams: {
        groupBy: 'Location',
        services: serviceName,
      },
    });
  }
}
